import React, { useState, useEffect } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import { FileDown, Pencil, Check, Sparkles, ArrowLeft, Mail, Copy, Loader2, RefreshCw, LayoutTemplate, LayoutGrid, Undo2 } from "lucide-react";
import ResumeHealth from "./ResumeHealth";
import TemplatedResumePreview from "./TemplatedResumePreview";
import TemplateThumbnail from "./TemplateThumbnail";
import TemplateGallery, { describeTemplate } from "./TemplateGallery";
import SuggestionTray from "./SuggestionTray";
import ApplySteps from "./ApplySteps";

const TargetedResumeWorkbench = ({ genId, roleTitle, resume, onBack, mode, onRegenerate, regenerating, membership, onMembershipRefresh, immediateActions, preferredTemplate }) => {
  const [data, setData] = useState(resume);
  const [editing, setEditing] = useState(false);
  const [exporting, setExporting] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templateError, setTemplateError] = useState("");
  const [meta, setMeta] = useState({ display_title: "", vacancy_title: "", apply_url: "", company_name: "" });
  const [canUndo, setCanUndo] = useState(false);
  const [applying, setApplying] = useState("");
  const [batchProgress, setBatchProgress] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [dismissed, setDismissed] = useState([]);
  const [steps, setSteps] = useState({ resume: false, cover: false });

  useEffect(() => {
    api.get("/templates").then(r => {
      const list = r.data.map(t => ({ ...t, serif: t.font === "serif", nameAlign: t.name_align }));
      setTemplates(list);
      setTemplateId(list.find(t => t.id === preferredTemplate)?.id || (list.find(t => t.layout === "single") || list[0])?.id || "");
      if (!list.length) setTemplateError("No templates are enabled for your membership. Please contact the administrator.");
    }).catch(() => setTemplateError("Templates could not be loaded. Please return to the dashboard and retry."));
    api.get(`/generated/${genId}`).then(r => {
      if (r.data.cover_letter) { setCover(r.data.cover_letter); setCoverTone(r.data.cover_letter_tone || "formal"); setSteps(s => ({ ...s, cover: true })); }
      setMeta({ display_title: r.data.display_title || "", vacancy_title: r.data.vacancy_title || "", apply_url: r.data.apply_url || "", company_name: r.data.company_name || "" });
      setCanUndo(!!r.data.can_undo);
      if (r.data.resume) setData(r.data.resume);
    }).catch(() => {});
  }, [genId]);

  const [coverOpen, setCoverOpen] = useState(false);
  const [cover, setCover] = useState(null);
  const [coverTone, setCoverTone] = useState("formal");
  const [coverLoading, setCoverLoading] = useState(false);
  const [coverExporting, setCoverExporting] = useState("");
  const [recruiterOpen, setRecruiterOpen] = useState(false);
  const [recruiter, setRecruiter] = useState(null);
  const [recruiterTone, setRecruiterTone] = useState("direct");
  const [recruiterLoading, setRecruiterLoading] = useState(false);

  const tpl = templates.find(t => t.id === templateId);
  // The row shows a handful; everything else lives in the gallery so the strip stays readable.
  const shortlistTemplates = React.useMemo(() => {
    const picked = templates.filter(t => t.id === templateId);
    return [...picked, ...templates.filter(t => t.id !== templateId)].slice(0, 6);
  }, [templates, templateId]);
  const headline = meta.display_title || data.target_title || roleTitle;
  const generatedHealth = data.health?.dimensions;
  const [healthLoading, setHealthLoading] = useState(false);
  useEffect(() => {
    if (!templateId) return;
    const controller = new AbortController();
    setHealthLoading(true);
    api.get(`/generated/${genId}/health`, { params: { template: templateId }, signal: controller.signal })
      .then(r => setData(d => ({ ...d, health: r.data })))
      .catch(() => { if (!controller.signal.aborted) toast.error("Could not refresh template health"); })
      .finally(() => { if (!controller.signal.aborted) setHealthLoading(false); });
    return () => controller.abort();
  }, [genId, templateId]);

  const updateBullet = (ei, bi, val) => {
    setData((d) => {
      const exp = [...d.experience];
      const bullets = [...exp[ei].bullets];
      bullets[bi] = val;
      exp[ei] = { ...exp[ei], bullets };
      return { ...d, experience: exp };
    });
  };
  const updateSummary = (val) => setData((d) => ({ ...d, summary: val }));

  const save = async () => {
    try {
      const { data: result } = await api.put(`/generated/${genId}`, { resume: data, template: templateId });
      setData(d => ({ ...d, health: result.health }));
      toast.success("Changes saved");
      setEditing(false);
    } catch (e) { toast.error("Could not save changes"); }
  };

  const applyChange = async (key, body, successMessage) => {
    setApplying(key);
    try {
      const { data: result } = await api.post(`/generated/${genId}/apply`, { ...body, template: templateId });
      setData(result.resume);
      setCanUndo(result.can_undo);
      toast.success(successMessage || result.change_note);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply that change");
    } finally { setApplying(""); }
  };

  const applyBatch = async (texts, keys) => {
    if (!texts.length) return;
    setApplying("batch");
    setBatchProgress({ done: 0, total: 1 });
    try {
      const { data: job } = await api.post(`/generated/${genId}/apply-batch`, { items: texts, template: templateId });
      setBatchProgress({ done: 0, total: job.total });
      for (let attempt = 0; attempt < 150; attempt += 1) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: status } = await api.get(`/generated/${genId}/apply-batch/${job.job_id}`);
        setBatchProgress({ done: status.done, total: status.total });
        if (status.status === "error") {
          toast.error(status.error || "Could not apply those changes");
          return;
        }
        if (status.status === "done") {
          setData(status.resume);
          setCanUndo(status.can_undo);
          setDismissed((d) => [...d, ...keys]);
          toast.success(status.change_note);
          return;
        }
      }
      toast.error("This is taking longer than expected. Refresh in a moment to see your updated resume.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply those changes");
    } finally { setApplying(""); setBatchProgress(null); }
  };

  const openRecruiterEmail = async () => {
    setRecruiterOpen(true);
    if (recruiter) return;
    setRecruiterLoading(true);
    try {
      const { data: result } = await api.post(`/generated/${genId}/recruiter-email?tone=${recruiterTone}`);
      setRecruiter(result.email);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not draft the recruiter note"); }
    finally { setRecruiterLoading(false); }
  };

  const recruiterRetone = async (tone) => {
    setRecruiterTone(tone);
    setRecruiterLoading(true);
    try {
      const { data: result } = await api.post(`/generated/${genId}/recruiter-email?tone=${tone}`);
      setRecruiter(result.email);
    } catch (e) { toast.error("Could not change tone"); }
    finally { setRecruiterLoading(false); }
  };

  const logApplication = () => {
    setSteps(s => ({ ...s, applied: true }));
    api.post("/applications", {
      company: meta.company_name || data.name || "Employer", title: meta.vacancy_title || headline,
      url: meta.apply_url, gen_id: genId, source: "workbench", status: "applied",
    }).then(() => toast.success("Logged in your Application Tracker")).catch(() => {});
  };

  const undo = async () => {
    setApplying("undo");
    try {
      const { data: result } = await api.post(`/generated/${genId}/undo`, null, { params: { template: templateId } });
      setData(result.resume);
      setCanUndo(result.can_undo);
      toast.success("Last change reverted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Nothing to undo"); }
    finally { setApplying(""); }
  };

  const download = async (path, filename, setFlag, flag) => {
    setFlag(flag);
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Download ready");
      return true;
    } catch (e) {
      let message = "Export failed. Please try again.";
      try { const body = JSON.parse(await e.response.data.text()); message = body.detail || message; } catch {}
      toast.error(message);
      return false;
    }
    finally { setFlag(""); }
  };

  const doExport = async (format) => {
    const name = `${(data.name || "resume").replace(/\s+/g, "_")}_${headline.replace(/\s+/g, "_")}_${templateId}.${format}`;
    const ok = await download(`/generated/${genId}/export?format=${format}&template=${templateId}`, name, setExporting, format);
    if (ok) setSteps(s => ({ ...s, resume: true }));
  };

  const fetchCover = async (tone, regenerate) => {
    setCoverLoading(true);
    try {
      const res = await api.post(`/generated/${genId}/cover-letter?tone=${tone}${regenerate ? "&regenerate=true" : ""}`);
      setCover(res.data.cover_letter);
      setCoverTone(tone);
      setSteps(s => ({ ...s, cover: true }));
      if (regenerate) toast.success("Cover letter updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Cover letter failed"); }
    finally { setCoverLoading(false); onMembershipRefresh?.(); }
  };

  const openCover = async () => {
    setCoverOpen(true);
    if (!cover) await fetchCover(coverTone, false);
  };

  const changeTone = async (tone) => {
    if (tone === coverTone && cover) return;
    await fetchCover(tone, false);
  };

  const regenCover = () => fetchCover(coverTone, true);

  const copyCover = () => {
    navigator.clipboard?.writeText(cover?.full_text || "");
    toast.success("Copied to clipboard");
  };

  const exportCover = (format) => {
    const name = `${(data.name || "cover").replace(/\s+/g, "_")}_cover_letter.${format}`;
    download(`/generated/${genId}/cover-letter/export?format=${format}&template=${templateId}`, name, setCoverExporting, format);
  };

  return (
    <div className="space-y-4" data-testid="workbench-container">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors self-start py-1" data-testid="workbench-back">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
          {canUndo && (
            <Button onClick={undo} disabled={!!applying} variant="outline" className="h-11 sm:h-9 border-slate-300 text-slate-700" data-testid="undo-change-button">
              <Undo2 className="w-4 h-4 mr-1.5" /> {applying === "undo" ? "Undoing…" : "Undo last change"}
            </Button>
          )}
          {editing ? (
            <Button onClick={save} className="h-11 sm:h-9 bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="save-edits-button"><Check className="w-4 h-4 mr-1.5" /> Save</Button>
          ) : (
            <Button onClick={() => setEditing(true)} variant="outline" className="h-11 sm:h-9 border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="edit-toggle-button"><Pencil className="w-4 h-4 mr-1.5" /> Edit manually</Button>
          )}
        </div>
      </div>

      <div className="flex items-start gap-2">
        <Sparkles className="w-4 h-4 text-sky-600 shrink-0 mt-1" />
        <div className="min-w-0">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 break-words" data-testid="workbench-heading">{mode === "reformat" ? "Your resume, new template" : mode === "improve" ? "Your current resume, improved" : `Resume tailored for ${headline}`}</h2>
          {meta.vacancy_title && meta.vacancy_title !== headline && (
            <p className="text-xs text-slate-500 mt-1 break-words" data-testid="workbench-vacancy-title">Applying to: {meta.vacancy_title}{meta.company_name ? ` · ${meta.company_name}` : ""}</p>
          )}
        </div>
      </div>

      {/* Template selector */}
      <div className="rounded-2xl bg-white border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-3" data-testid="template-selector">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><LayoutTemplate className="w-3.5 h-3.5" /> Template · download in your chosen style</p>
          {templates.length > 0 && (
            <Button onClick={() => setGalleryOpen(true)} variant="outline" className="h-9 rounded-xl border-slate-300 text-xs text-slate-700" data-testid="browse-templates-button">
              <LayoutGrid className="mr-1.5 h-3.5 w-3.5" /> Browse all {templates.length} templates
            </Button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {templateError && <p className="col-span-full text-sm text-amber-700" data-testid="template-load-error">{templateError}</p>}
          {!templates.length && !templateError && <p className="col-span-full text-sm text-slate-500" data-testid="templates-loading">Loading your membership templates…</p>}
          {shortlistTemplates.map((t) => (
            <button
              key={t.id}
              onClick={() => setTemplateId(t.id)}
              className={`group flex flex-col items-center rounded-xl p-2 border transition-colors ${templateId === t.id ? "border-sky-300 bg-sky-50 ring-2 ring-sky-200" : "border-slate-200 hover:border-slate-400 hover:bg-slate-50"}`}
              data-testid={`template-option-${t.id}`}
              title={t.name}
            >
              <div className="flex justify-center rounded-lg border border-slate-200 bg-white p-1.5">
                <TemplateThumbnail tpl={t} size="md" />
              </div>
              <span className={`mt-1.5 block w-full text-center text-[11px] leading-tight break-words ${templateId === t.id ? "text-sky-700" : "text-slate-600"}`}>{describeTemplate(t.name).label}</span>
            </button>
          ))}
        </div>
        {tpl && <p className="mt-2 text-[11px] text-slate-500" data-testid="selected-template-note">Selected: {tpl.name}{templates.length > shortlistTemplates.length ? ` · ${templates.length - shortlistTemplates.length} more in the full gallery` : ""}</p>}
      </div>

      <TemplateGallery open={galleryOpen} onOpenChange={setGalleryOpen} templates={templates} value={templateId} onSelect={setTemplateId} />

      <div className="grid gap-4 lg:grid-cols-3 items-start">
        <div className="order-2 lg:order-1 lg:col-span-2">
          {tpl && <TemplatedResumePreview data={data} tpl={tpl} editing={editing} onSummary={updateSummary} onBullet={updateBullet} />}
          {editing && <p className="mt-2 text-xs text-amber-700" data-testid="unsaved-edits-notice">Save your edits to refresh Resume Health and enable downloads.</p>}
        </div>

        <div className="order-1 lg:order-2 space-y-4">
          <div className="h-full" data-testid="generated-resume-health">
            <ResumeHealth health={generatedHealth} />
            {healthLoading && <p className="text-xs text-slate-500 mt-2" data-testid="generated-health-loading">Checking selected template…</p>}
            <p className="text-[11px] text-slate-500 mt-2">Generated resume · transparent heuristics, not a guaranteed ATS score or hiring outcome.</p>
          </div>

        </div>
      </div>

      {mode !== "reformat" && <SuggestionTray
        keywords={data.health?.missing_keywords}
        skills={data.skills}
        additions={data.suggested_additions}
        actions={immediateActions}
        dismissed={dismissed}
        applying={applying}
        batchProgress={batchProgress}
        onDismiss={(key) => setDismissed(d => [...d, key])}
        onKeyword={(term, remove) => applyChange(`kw:${term}`, { mode: "keyword", text: term, remove }, remove ? `Removed "${term}"` : `Added "${term}" to Core Skills`)}
        onInstruction={(key, text) => applyChange(key, { mode: "instruction", text })}
        onBatch={applyBatch}
      />}

      <ApplySteps
        steps={steps}
        exporting={exporting}
        editing={editing}
        hasTemplate={!!tpl}
        regenerating={regenerating}
        onRegenerate={mode === "reformat" ? null : onRegenerate}
        hideApply={mode === "reformat"}
        onExport={doExport}
        onCover={openCover}
        coverReady={!!cover}
        applyUrl={meta.apply_url}
        company={meta.company_name}
        resumeLimitReached={membership?.remaining.resume === 0}
        onRecruiterEmail={openRecruiterEmail}
        recruiterReady={!!recruiter}
        onApplyClick={logApplication}
      />

      {data.tailoring_notes?.length > 0 && <section className="rounded-2xl bg-white border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5" data-testid="tailoring-notes"><h3 className="text-base font-bold text-slate-900 mb-3">What Changed</h3><ul className="grid md:grid-cols-2 gap-3">{data.tailoring_notes.map((n, i) => <li key={i} className="flex gap-2 text-sm text-slate-500"><Sparkles className="w-3.5 h-3.5 text-sky-600 shrink-0 mt-1" />{n}</li>)}</ul></section>}

      <Dialog open={recruiterOpen} onOpenChange={setRecruiterOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl max-h-[88dvh] overflow-y-auto p-4 sm:p-6" data-testid="recruiter-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-sky-600" /> Recruiter note</DialogTitle>
            <DialogDescription className="text-slate-500">A short intro to send with your application — grounded only in your resume.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-2" data-testid="recruiter-tone-selector">
            <span className="text-xs text-slate-500">Tone:</span>
            {["direct", "warm", "brief"].map(tn => (
              <button key={tn} onClick={() => recruiterRetone(tn)} disabled={recruiterLoading} className={`px-2.5 py-1 rounded-md text-xs capitalize border transition-colors disabled:opacity-50 ${recruiterTone === tn ? "border-sky-200 bg-sky-50 text-sky-700" : "border-slate-300 text-slate-700"}`} data-testid={`recruiter-tone-${tn}`}>{tn}</button>
            ))}
          </div>
          {recruiterLoading ? (
            <div className="py-10 flex flex-col items-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin text-sky-600 mb-2" /><p className="text-sm">Drafting your note…</p></div>
          ) : recruiter ? (
            <div className="space-y-3">
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="recruiter-subject">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Subject</p>
                <p className="text-sm text-slate-900 mt-1">{recruiter.subject}</p>
              </div>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 whitespace-pre-wrap text-sm text-slate-800 leading-relaxed max-h-[40vh] overflow-y-auto" data-testid="recruiter-body">{recruiter.body}</div>
              {recruiter.linkedin_dm && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="recruiter-dm">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">LinkedIn message</p>
                  <p className="text-sm text-slate-700 mt-1">{recruiter.linkedin_dm}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 sm:flex">
                <Button onClick={() => { navigator.clipboard?.writeText(`${recruiter.subject}\n\n${recruiter.body}`); toast.success("Copied"); }} variant="outline" className="h-11 border-slate-300 text-slate-700" data-testid="recruiter-copy"><Copy className="w-4 h-4 mr-1.5" /> Copy note</Button>
                <Button onClick={() => { navigator.clipboard?.writeText(recruiter.linkedin_dm || ""); toast.success("Copied"); }} variant="outline" className="h-11 border-slate-300 text-slate-700" data-testid="recruiter-copy-dm"><Copy className="w-4 h-4 mr-1.5" /> Copy DM</Button>
              </div>
            </div>
          ) : <p className="text-sm text-slate-500 py-6" data-testid="recruiter-empty">No note yet — close and reopen to retry.</p>}
        </DialogContent>
      </Dialog>

      {/* Cover letter dialog */}
      <Dialog open={coverOpen} onOpenChange={setCoverOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl max-h-[88dvh] overflow-y-auto p-4 sm:p-6" data-testid="cover-letter-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-sky-600" /> Cover letter for {headline}</DialogTitle>
            <DialogDescription className="text-slate-500">Grounded only in your resume — pick a tone, then copy or download.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 flex-wrap" data-testid="cover-tone-selector">
            <span className="text-xs text-slate-500">Tone:</span>
            {["formal", "warm", "concise"].map((tn) => (
              <button
                key={tn}
                onClick={() => changeTone(tn)}
                disabled={coverLoading || (membership?.remaining.cover === 0 && tn !== coverTone)}
                className={`px-2.5 py-1 rounded-md text-xs capitalize border transition-colors disabled:opacity-50 ${coverTone === tn ? "border-sky-200 bg-sky-50 text-sky-700" : "border-slate-300 text-slate-700 hover:border-slate-300"}`}
                data-testid={`cover-tone-${tn}`}
              >
                {tn}
              </button>
            ))}
          </div>
          {coverLoading ? (
            <div className="py-12 flex flex-col items-center justify-center text-slate-500">
              <Loader2 className="w-7 h-7 animate-spin text-sky-600 mb-3" />
              <p className="text-sm">Writing your cover letter…</p>
            </div>
          ) : cover ? (
            <>
              <div className="max-h-[45vh] sm:max-h-[50vh] overflow-y-auto rounded-lg bg-slate-50 border border-slate-200 p-4 whitespace-pre-wrap text-sm text-slate-800 leading-relaxed" data-testid="cover-letter-text">
                {cover.full_text}
              </div>
              <div className="grid grid-cols-2 gap-2 pt-2 sm:flex sm:flex-wrap sm:items-center">
                <Button onClick={copyCover} variant="outline" className="h-11 sm:h-9 border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="cover-copy-button"><Copy className="w-4 h-4 mr-1.5" /> Copy</Button>
                <Button onClick={regenCover} disabled={coverLoading || membership?.remaining.cover === 0} variant="outline" className="h-11 sm:h-9 border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="cover-regenerate-button"><RefreshCw className="w-4 h-4 mr-1.5" /> Regenerate</Button>
                <div className="hidden sm:block sm:flex-1" />
                <Button onClick={() => exportCover("pdf")} disabled={!!coverExporting || !tpl} className="h-11 sm:h-9 bg-sky-600 hover:bg-sky-500 text-white" data-testid="cover-export-pdf"><FileDown className="w-4 h-4 mr-1.5" /> {coverExporting === "pdf" ? "…" : "PDF"}</Button>
                <Button onClick={() => exportCover("docx")} disabled={!!coverExporting || !tpl} variant="outline" className="h-11 sm:h-9 border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="cover-export-docx"><FileDown className="w-4 h-4 mr-1.5" /> {coverExporting === "docx" ? "…" : "DOCX"}</Button>
              </div>
            </>
          ) : <p className="text-sm text-slate-500 py-6" data-testid="cover-letter-empty">{membership?.remaining.cover === 0 ? "Your cover-letter allowance is used. Request Growth from the membership panel for unlimited generation." : "No cover letter yet. Close and reopen this panel to try again."}</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TargetedResumeWorkbench;
